import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@heroui/react';
import { Button } from '@/components/Button';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { avatarHue } from '@/lib/avatarHue';
import { formatDateTime } from '@/components/serverSettings/auditFormatting';
import { LoadError } from '@/components/ui/LoadError';
import { FlagIcon } from '@/components/icons';
import type {
  ReportItem,
  ReportStatus,
  ReportQueueQuery,
} from '@bunker/shared';

/**
 * Moderation queue — the moderator-facing list of reports. Shared by the
 * guild "Жалобы" tab (surface 'guild') and the platform "Модерация Бункера"
 * page (surface 'platform'). Read + resolve are authorized server-side; this
 * component trusts the access decision made by its host.
 */

const STATUS_FILTERS: (ReportStatus | 'all')[] = ['open', 'reviewing', 'actioned', 'dismissed', 'all'];

export function reportQueueKey(surface: 'guild' | 'platform', guildId: string | null, status: string) {
  return ['reports', surface, guildId ?? '∅', status] as const;
}

export function ReportQueue(props: { surface: 'guild' | 'platform'; guildId?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<ReportStatus | 'all'>('open');

  const query: ReportQueueQuery = {
    surface: props.surface,
    ...(props.guildId ? { guildId: props.guildId } : {}),
    ...(status !== 'all' ? { status } : {}),
  };

  // Paginated: the server returns a `nextCursor`; without this the queue was
  // capped to the first page and older reports were invisible from the UI.
  const q = useInfiniteQuery({
    queryKey: reportQueueKey(props.surface, props.guildId ?? null, status),
    queryFn: ({ pageParam }) =>
      api.listReports(pageParam ? { ...query, cursor: pageParam } : query),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['reports', props.surface] });

  // Track the report being resolved so only ITS buttons disable (not the whole
  // list) — per-card feedback instead of one shared spinner.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const resolveM = useMutation({
    mutationFn: (vars: { id: string; status: 'reviewing' | 'actioned' | 'dismissed' }) =>
      api.resolveReport(vars.id, { status: vars.status }),
    onMutate: (vars) => setPendingId(vars.id),
    onSuccess: () => {
      invalidate();
      toast.success(t('mod.resolved'));
    },
    onError: (err) => toast.error(...errToToast(err, t('mod.resolveError'))),
    onSettled: () => setPendingId(null),
  });

  // Depend on the stable `mutate` fn, not the mutation object (which is a new
  // reference every render) — otherwise onResolve changes each render and every
  // memo(ReportCard) re-renders on any parent state flip (e.g. pendingId).
  const resolveMutate = resolveM.mutate;
  const onResolve = useCallback(
    (id: string, s: 'reviewing' | 'actioned' | 'dismissed') => resolveMutate({ id, status: s }),
    [resolveMutate],
  );

  // A page without `reports` (a proxy error page, an old server, a stub) is an
  // empty page, not a crash of the whole settings screen.
  const reports = q.data?.pages.flatMap((p) => (Array.isArray(p?.reports) ? p.reports : [])) ?? [];

  return (
    <div className="space-y-3">
      {/* Status filter chips — active state carries a non-colour cue (weight +
          ring) so it isn't conveyed by colour alone (WCAG 1.4.1). */}
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('mod.filterLabel')}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
            className={
              'min-h-11 rounded-full px-3 py-2 text-xs transition-colors ' +
              (status === s
                ? 'font-semibold bg-accent/15 text-accent ring-1 ring-inset ring-accent/40'
                : 'text-ink-muted hover:bg-white/8 hover:text-ink')
            }
          >
            {t(`mod.filter.${s}`)}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner />
        </div>
      ) : q.isError ? (
        // Without this branch a failed fetch fell through to "Жалоб нет" — a
        // moderator saw an empty queue on a broken request and walked away.
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
          <div
            className="grid h-9 w-9 place-items-center rounded-full text-ink-muted"
            style={{ backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }}
          >
            <FlagIcon className="h-4 w-4" />
          </div>
          <p className="text-xs leading-relaxed text-ink-muted">{t('mod.empty')}</p>
        </div>
      ) : (
        <>
          {reports.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              busy={pendingId === r.id}
              onResolve={onResolve}
            />
          ))}
          {q.hasNextPage && (
            <div className="grid place-items-center pt-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? t('mod.loadingMore') : t('mod.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const ReportCard = memo(function ReportCard(props: {
  report: ReportItem;
  busy: boolean;
  onResolve: (id: string, status: 'reviewing' | 'actioned' | 'dismissed') => void;
}) {
  const { t } = useTranslation();
  const r = props.report;
  const reporter = r.reporter;
  const target = r.targetUser;
  const snap = (r.snapshot ?? null) as
    | { content?: string; author?: { username?: string; displayName?: string | null } }
    | null;

  const open = r.status === 'open' || r.status === 'reviewing';

  return (
    <div
      className="app-report-card space-y-2.5 rounded-xl p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ReasonBadge reason={r.reason} />
        {r.platformEscalated && (
          <span className="font-medium rounded-full bg-danger/15 px-2 py-0.5 text-[10px] !text-danger-ink">
            {t('mod.escalated')}
          </span>
        )}
        <StatusBadge status={r.status} />
        <span className="ml-auto text-[11px] tabular-nums text-ink-muted">
          {formatDateTime(r.createdAt)}
        </span>
      </div>

      {/* What's being reported */}
      <div
        className="rounded-lg p-2.5 text-xs"
        style={{ backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }}
      >
        {r.kind === 'message' ? (
          <>
            <div className="mb-1 text-[11px] text-ink-muted">
              {t('mod.messageFrom', {
                name: snap?.author?.displayName || snap?.author?.username || target?.username || '—',
              })}
            </div>
            <div className="whitespace-pre-wrap break-words text-ink-dim">
              {snap?.content || <span className="italic text-ink-muted">{t('mod.noText')}</span>}
            </div>
          </>
        ) : (
          <ReportedUser target={target} />
        )}
      </div>

      {/* Reporter's note */}
      {r.note && (
        <div className="text-xs text-ink-dim">
          <span className="text-ink-muted">{t('mod.note')}: </span>
          {r.note}
        </div>
      )}

      {/* Footer: who reported + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ink-muted">
          {t('mod.reportedBy', {
            name: reporter ? reporter.displayName || reporter.username : '—',
          })}
        </span>
        {r.guild && (
          <span className="text-[11px] text-ink-muted">· {r.guild.name}</span>
        )}
        {open && (
          <div className="app-report-actions ml-auto flex items-center gap-1.5">
            {r.status === 'open' && (
              <Button
                variant="ghost"
                size="sm"
                disabled={props.busy}
                onClick={() => props.onResolve(r.id, 'reviewing')}
              >
                {t('mod.action.review')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              disabled={props.busy}
              onClick={() => props.onResolve(r.id, 'dismissed')}
            >
              {t('mod.action.dismiss')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={props.busy}
              onClick={() => props.onResolve(r.id, 'actioned')}
            >
              {t('mod.action.act')}
            </Button>
          </div>
        )}
        {!open && r.reviewer && (
          <span className="ml-auto text-[11px] text-ink-muted">
            {t('mod.resolvedBy', { name: r.reviewer.displayName || r.reviewer.username })}
          </span>
        )}
      </div>
    </div>
  );
});

function ReportedUser(props: { target: ReportItem['targetUser'] }) {
  const { t } = useTranslation();
  const u = props.target;
  if (!u) return <span className="italic text-ink-muted">{t('mod.userGone')}</span>;
  const hue = avatarHue(u.id);
  const label = u.displayName || u.username;
  return (
    <div className="flex items-center gap-2.5">
      <div
        className={
          'grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full text-xs ' +
          (u.avatarUrl ? 'text-ink' : `${hue.bg} ${hue.text}`)
        }
      >
        {u.avatarUrl ? (
          <img src={u.avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          label.charAt(0).toUpperCase()
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate text-ink">{label}</div>
        <div className="truncate text-[11px] text-ink-muted">@{u.username}</div>
      </div>
    </div>
  );
}

function ReasonBadge(props: { reason: ReportItem['reason'] }) {
  const { t } = useTranslation();
  return (
    <span className="font-medium rounded-full bg-accent/12 px-2 py-0.5 text-[10px] text-accent">
      {t(`report.reason.${props.reason}`)}
    </span>
  );
}

function StatusBadge(props: { status: ReportStatus }) {
  const { t } = useTranslation();
  const tone =
    props.status === 'actioned'
      ? 'bg-success/15 !text-success-ink'
      : props.status === 'dismissed'
        ? 'bg-white/8 text-ink-muted'
        : props.status === 'reviewing'
          ? 'bg-warning/15 !text-warning-ink'
          : 'bg-accent/12 text-accent';
  return (
    <span className={`font-medium rounded-full px-2 py-0.5 text-[10px] ${tone}`}>
      {t(`mod.status.${props.status}`)}
    </span>
  );
}
